function firebaseAuthErrorCode(error: unknown): string {
  return typeof error === "object" && error && "code" in error
    ? String(error.code)
    : "";
}

export function microsoftSignInSupportReference(error: unknown): string {
  const references: Record<string, string> = {
    "auth/invalid-credential": "MS-101",
    "auth/invalid-oauth-client-id": "MS-102",
    "auth/internal-error": "MS-103",
    "auth/invalid-auth-event": "MS-104",
    "auth/web-storage-unsupported": "MS-105",
    "auth/tenant-id-mismatch": "MS-106",
    "auth/missing-or-invalid-nonce": "MS-107",
    "auth/redirect-cancelled-by-user": "MS-108",
    "auth/credential-already-in-use": "MS-109",
  };
  return references[firebaseAuthErrorCode(error)] ?? "MS-199";
}

export function microsoftSignInErrorMessage(error: unknown): string {
  const code = firebaseAuthErrorCode(error);

  switch (code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Microsoft sign-in was canceled. Try again when you’re ready.";
    case "auth/popup-blocked":
      return "Your browser blocked the Microsoft sign-in window. Allow pop-ups for this site and try again.";
    case "auth/network-request-failed":
      return "We could not reach Microsoft sign-in. Check your connection and try again.";
    case "auth/unauthorized-domain":
    case "auth/operation-not-allowed":
    case "planner/microsoft-tenant-missing":
    case "planner/microsoft-verification-missing":
      return "Microsoft sign-in is not available for this site. Ask your workspace administrator to check the sign-in configuration.";
    case "auth/account-exists-with-different-credential":
      return "This email already uses another sign-in method. Use that method or ask your workspace administrator for help.";
    case "auth/user-disabled":
      return "This account is disabled. Ask your workspace administrator to restore access.";
    default:
      return `We could not complete Microsoft sign-in. Try again. If it keeps happening, ask your workspace administrator for help and include reference ${microsoftSignInSupportReference(error)}.`;
  }
}

export function microsoftWorkspaceErrorMessage(error: unknown): string {
  switch (firebaseAuthErrorCode(error)) {
    case "MICROSOFT_IDENTITY_NOT_ELIGIBLE":
      return "This Microsoft account is not eligible for the VIPdesk workspace. If you believe this is a mistake, ask a workspace administrator for help.";
    case "MICROSOFT_IDENTITY_VERIFICATION_UNAVAILABLE":
      return "We could not verify your Microsoft account right now. Your account is safe; try again in a few minutes.";
    case "WORKSPACE_IDENTITY_NOT_READY":
      return "Microsoft sign-in is not ready for this workspace. Ask a workspace administrator for help.";
    default:
      return microsoftSignInErrorMessage(error);
  }
}

export function externalMfaErrorMessage(
  error: unknown,
  action: "sign-in" | "enroll" | "remove",
): string {
  switch (firebaseAuthErrorCode(error)) {
    case "planner/invalid-totp-code":
      return "Enter the 6-digit code from your authenticator app.";
    case "auth/invalid-verification-code":
    case "auth/invalid-multi-factor-info":
      return "That code is not valid. Check the current code in your authenticator app and try again.";
    case "auth/code-expired":
    case "auth/session-expired":
    case "auth/invalid-multi-factor-session":
    case "auth/missing-multi-factor-session":
      return action === "sign-in"
        ? "This sign-in attempt expired. Go back and sign in again."
        : "This security setup attempt expired. Start again.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "That password is not correct. Check it and try again.";
    case "auth/requires-recent-login":
      return "For your security, sign out and sign in again before changing your authenticator app.";
    case "auth/unverified-email":
      return "Your invited email must be verified before you can add an authenticator app. Ask a project owner to resend your invitation if setup is incomplete.";
    case "auth/too-many-requests":
      return "Too many attempts were made. Wait a few minutes, then try again.";
    case "auth/network-request-failed":
      return "We could not reach the sign-in service. Check your connection and try again.";
    case "planner/external-account-required":
      return "Authenticator-app security is managed here only for client and external accounts.";
    case "planner/mfa-already-enabled":
      return "An authenticator app is already enabled for this account.";
    case "planner/mfa-factor-not-found":
      return "That authenticator app is no longer connected. Refresh the page to see the current status.";
    default:
      return action === "sign-in"
        ? "We could not verify that code. Try the current code from your authenticator app."
        : action === "enroll"
          ? "We could not add your authenticator app. No security settings were changed. Try again."
          : "We could not remove your authenticator app. Your existing sign-in protection is still active.";
  }
}
